import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { settingsAPI, authAPI } from '../services/api';
import { useAuth } from '../services/AuthContext';
import Autocomplete from 'react-google-autocomplete';
import ClientSidebar from '../components/Dashboard/ClientSidebar';

const UserSettings = () => {
  const { coachId } = useParams();
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [profileData, setProfileData] = useState({
    name: '',
    city: '',
    language: '',
    dob: ''
  });

  const coaches = {
    1: { id: 1, name: "Ashley Copley", heygenAvatar: "ca655ceed9e24b0eab82950b464c8e17", specialty: "AI Coaching Companion" },
    2: { id: 2, name: "Michael Legge", heygenAvatar: "a2465792bc8e4b80805376278389386e", specialty: "Personal & Professional Growth" }
  };

  const currentCoach = coachId ? (coaches[coachId] || coaches[1]) : null;

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        city: user.city || '',
        language: user.language || '',
        dob: user.dob ? new Date(user.dob).toISOString().split('T')[0] : ''
      });
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const dataToSend = { ...profileData };
      if (dataToSend.dob) {
        const dobDate = new Date(dataToSend.dob);
        if (!isNaN(dobDate.getTime())) dataToSend.dob = dobDate.toISOString();
      }
      const response = await authAPI.updateMe(dataToSend);
      updateUser(response.data.user);
      setSuccess('Profile updated successfully');
    } catch (err) {
      setError(err.response?.data?.message || 'Error updating profile');
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Only images are allowed for profile picture');
      return;
    }
    setAvatarUploading(true);
    setError('');
    setSuccess('');
    try {
      const response = await settingsAPI.uploadAvatar(file);
      updateUser({ ...user, avatarUrl: response.data.avatarUrl });
      setSuccess('Profile picture updated');
    } catch (err) {
      setError('Error uploading avatar');
    } finally {
      setAvatarUploading(false);
      event.target.value = '';
    }
  };

  const getFullUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http') || path.startsWith('blob:')) return path;
    return `${process.env.REACT_APP_API_URL}${path}`;
  };

  return (
    <div className="relative flex size-full min-h-screen flex-col bg-[var(--bg-main)] group/design-root overflow-x-hidden font-display text-[var(--text-primary)]">
      <div className="layout-container flex h-full grow flex-col">
        <div className="px-4 sm:px-6 md:px-8 lg:px-12 flex flex-1 justify-center py-5 w-full">
          <div className="layout-content-container flex flex-col lg:flex-row w-full max-w-[1200px] flex-1 gap-8">
            
            <main className="flex-1 flex flex-col">
              <header className="mb-8 flex items-center justify-between">
                <div>
                   <h1 className="text-2xl md:text-3xl font-black text-[var(--text-primary)] tracking-tight">Account Settings</h1>
                   <p className="text-[var(--text-secondary)] text-sm mt-1">Manage your personal information and preferences.</p>
                </div>
                <button 
                   onClick={handleSave}
                   disabled={loading}
                   className="bg-[var(--tangerine)] text-white font-bold px-8 py-3 rounded-2xl shadow-lg shadow-orange-500/20 hover:scale-105 active:scale-95 transition-all text-sm disabled:opacity-50"
                 >
                   {loading ? 'Saving...' : 'Save Changes'}
                 </button>
              </header>

              {(error || success) && (
                <div className={`px-6 py-4 rounded-[1.5rem] mb-8 font-bold text-sm flex items-center gap-3 animate-fade-in ${error ? 'bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20' : 'bg-green-500/10 text-green-600 dark:text-green-400 border border-green-500/20'}`}>
                  <span className="material-symbols-outlined">{error ? 'error' : 'check_circle'}</span>
                  {error || success}
                </div>
              )}

              <div className="space-y-8 pb-10">
                {/* Profile Picture Section */}
                <div className="bg-[var(--surface-white)] rounded-[2.5rem] shadow-xl shadow-black/5 border border-[var(--border-light)] p-8">
                   <div className="flex flex-col md:flex-row items-center gap-8">
                      <div className="w-32 h-32 rounded-[2rem] bg-[var(--off-white)] overflow-hidden border-4 border-[var(--surface-white)] shadow-2xl shrink-0 relative group/avatar">
                         {user?.avatarUrl ? (
                           <img src={getFullUrl(user.avatarUrl)} alt="Profile" className="w-full h-full object-cover" />
                         ) : (
                           <div className="w-full h-full flex items-center justify-center text-[var(--text-secondary)]/60">
                              <span className="material-symbols-outlined text-5xl">person</span>
                           </div>
                         )}
                         {avatarUploading && (
                           <div className="absolute inset-0 bg-black/40 flex items-center justify-center backdrop-blur-sm">
                             <div className="w-8 h-8 border-2 border-[var(--surface-white)]/20 border-t-white rounded-full animate-spin"></div>
                           </div>
                         )}
                         <label className="absolute inset-0 bg-black/0 group-hover/avatar:bg-black/40 transition-all flex items-center justify-center opacity-0 group-hover/avatar:opacity-100 cursor-pointer">
                            <span className="material-symbols-outlined text-white">camera_alt</span>
                            <input type="file" accept="image/*" onChange={handleAvatarUpload} disabled={avatarUploading} className="hidden" />
                         </label>
                      </div>
                      <div className="flex-1 text-center md:text-left">
                         <h4 className="text-xl font-black text-[var(--text-primary)] mb-1">Your Profile Photo</h4>
                         <p className="text-sm text-[var(--text-secondary)] mb-6 italic">Visible to your coaches and in chat conversations.</p>
                         <div className="flex justify-center md:justify-start gap-3">
                           <label className="bg-[var(--off-white)] hover:bg-gray-200 text-gray-700 font-bold px-6 py-2.5 rounded-xl cursor-pointer transition-all text-sm">
                             Upload New Image
                             <input type="file" accept="image/*" onChange={handleAvatarUpload} disabled={avatarUploading} className="hidden" />
                           </label>
                         </div>
                      </div>
                   </div>
                </div>

                {/* Info Fields Section */}
                <div className="bg-[var(--surface-white)] rounded-[2.5rem] shadow-xl shadow-black/5 border border-[var(--border-light)] p-8 sm:p-10">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                       <div className="md:col-span-2">
                          <label className="text-[10px] font-black text-[var(--text-secondary)]/60 uppercase tracking-widest block mb-3 px-1">Full Name</label>
                          <input
                            type="text"
                            name="name"
                            value={profileData.name}
                            onChange={handleInputChange}
                            className="w-full bg-[var(--off-white)] border border-transparent focus:bg-[var(--surface-white)] focus:border-[var(--tangerine)] text-[var(--text-primary)] font-bold rounded-2xl p-4 transition-all outline-none"
                            placeholder="John Doe"
                          />
                       </div>
                       
                       <div>
                          <label className="text-[10px] font-black text-[var(--text-secondary)]/60 uppercase tracking-widest block mb-3 px-1">Location / City</label>
                          <Autocomplete
                            apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                            onPlaceSelected={(place) => {
                              setProfileData(prev => ({ ...prev, city: place.formatted_address || place.name || '' }));
                            }}
                            options={{ types: ['(cities)'] }}
                            name="city"
                            className="w-full bg-[var(--off-white)] border border-transparent focus:bg-[var(--surface-white)] focus:border-[var(--tangerine)] text-[var(--text-primary)] font-bold rounded-2xl p-4 transition-all outline-none"
                            value={profileData.city}
                            onChange={handleInputChange}
                            placeholder="Type a city..."
                          />
                       </div>

                       <div>
                          <label className="text-[10px] font-black text-[var(--text-secondary)]/60 uppercase tracking-widest block mb-3 px-1">Preferred Language</label>
                          <div className="relative">
                            <select
                              name="language"
                              value={profileData.language}
                              onChange={handleInputChange}
                              className="w-full bg-[var(--off-white)] border border-transparent focus:bg-[var(--surface-white)] focus:border-[var(--tangerine)] text-[var(--text-primary)] font-bold rounded-2xl p-4 transition-all outline-none appearance-none"
                            >
                              <option value="English">English</option>
                              <option value="Russian">Russian</option>
                              <option value="Spanish">Spanish</option>
                              <option value="Other">Other</option>
                            </select>
                            <span className="material-symbols-outlined absolute right-4 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]/60 pointer-events-none">expand_more</span>
                          </div>
                       </div>

                       <div className="md:col-span-2">
                          <label className="text-[10px] font-black text-[var(--text-secondary)]/60 uppercase tracking-widest block mb-3 px-1">Date of Birth</label>
                          <input
                            type="date"
                            name="dob"
                            value={profileData.dob}
                            onChange={handleInputChange}
                            className="w-full bg-[var(--off-white)] border border-transparent focus:bg-[var(--surface-white)] focus:border-[var(--tangerine)] text-[var(--text-primary)] font-bold rounded-2xl p-4 transition-all outline-none"
                          />
                       </div>
                    </div>
                </div>
              </div>
            </main>

            <ClientSidebar selectedCoach={currentCoach} activeTab="profile" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserSettings;
